import { Box, List, Avatar, ListItem, ListItemAvatar, ListItemText, IconButton, Typography } from '@mui/material';
import { Download as DownloadIcon, Delete as DeleteIcon, Folder as FolderIcon } from '@mui/icons-material';
import { getFileSizeInUnits } from '../utils/files';

type FileDisplayProps<T extends { name: string } = File> = {
  files: T[];
  onRemove?(file: T): void;
  onDownload?(file: T): void;
  maxFiles?: number;
};

const FileDisplay = <T extends { name: string } = File>({
  files,
  onRemove,
  onDownload,
  maxFiles,
}: FileDisplayProps<T>) => {
  const actions = [
    {
      icon: <DownloadIcon sx={{ position: 'relative', top: 1 }} />,
      label: 'Download file',
      onClick: onDownload ? (file: T) => onDownload(file) : null,
    },
    {
      icon: <DeleteIcon />,
      label: 'Remove file',
      onClick: onRemove ? (file: T) => onRemove(file) : null,
    },
  ].filter((action) => action.onClick);

  return (
    <Box>
      {maxFiles && (
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="body2" component="span">
            {files.length}/{maxFiles}
          </Typography>
        </Box>
      )}
      <Box border="1px solid #ccc" borderColor="grey.300" borderRadius={1}>
        <List sx={{ maxHeight: 220, overflowY: 'auto' }}>
          {files.map((file) => (
            <ListItem
              key={file.name}
              secondaryAction={
                actions.length > 0 ? (
                  <Box display="flex" gap={1}>
                    {actions.map((action) => (
                      <Box key={action.label}>
                        <IconButton edge="end" aria-label={action.label} onClick={() => action.onClick!(file)}>
                          {action.icon}
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                ) : null
              }
            >
              <ListItemAvatar>
                <Avatar>
                  <FolderIcon />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={file.name}
                secondary={'size' in file && typeof file.size === 'number' ? getFileSizeInUnits(file.size) : null}
                primaryTypographyProps={{
                  sx: {
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  },
                }}
              />
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );
};

export default FileDisplay;
